import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import App from "./App.tsx";
import "./index.css";
import { reportClientError } from "./lib/errorReporter";

const CHUNK_RELOAD_KEY = "__patakare_chunk_reload__";
const BRANDING_CACHE_KEY = "__patakare_branding_cache_cleared_v2__";
const PREVIEW_SW_RESET_KEY = "__patakare_preview_sw_reset__";

const isPreviewOrDevHost = () => {
  const host = window.location.hostname;
  return (
    import.meta.env.DEV ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.includes("lovableproject.com") ||
    host.includes("lovable.dev") ||
    host.startsWith("id-preview--")
  );
};

const clearPreviewServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) return;

  try {
    const wasControlledByWorker = Boolean(navigator.serviceWorker.controller);
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    if (wasControlledByWorker) {
      const now = Date.now();
      const previousReset = Number(sessionStorage.getItem(PREVIEW_SW_RESET_KEY) || 0);
      if (now - previousReset > 3000) {
        sessionStorage.setItem(PREVIEW_SW_RESET_KEY, String(now));
        window.location.reload();
      }
    }
  } catch (error) {
    console.warn("[SW] Preview cleanup failed:", error);
  }
};

try {
  if (localStorage.getItem(BRANDING_CACHE_KEY) !== "1") {
    localStorage.removeItem("patakare_branding_cache");
    localStorage.setItem(BRANDING_CACHE_KEY, "1");
  }
} catch { /* ignore storage access errors */ }

const isChunkLoadError = (message: string) =>
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d|Loading CSS chunk \d/i.test(message);

const forceChunkRecoveryReload = (reason: string) => {
  const now = Date.now();
  try {
    const prevRaw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const prev = prevRaw ? JSON.parse(prevRaw) : null;

    // Prevent infinite reload loops — max 1 reload per 30 seconds on same path
    if (prev?.path === window.location.pathname && now - Number(prev?.ts || 0) < 30000) {
      console.warn('[ChunkRecovery] Skipping reload — too recent:', reason);
      return;
    }

    console.warn('[ChunkRecovery] Reloading due to:', reason);
    sessionStorage.setItem(
      CHUNK_RELOAD_KEY,
      JSON.stringify({ ts: now, path: window.location.pathname, reason })
    );

    const url = new URL(window.location.href);
    url.searchParams.set("v", String(now));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
};

window.addEventListener("vite:preloadError", (event: Event) => {
  event.preventDefault?.();
  forceChunkRecoveryReload("vite:preloadError");
});

window.addEventListener("error", (event: ErrorEvent) => {
  const message = String(event.message || event.error?.message || "");
  if (isChunkLoadError(message)) {
    forceChunkRecoveryReload("window:error");
    return;
  }
  // Runtime errors outside React's render tree (event handlers, timers, etc.)
  // — AppErrorBoundary only catches render-tree crashes, so this is the only
  // place these are ever seen.
  reportClientError({
    type: "window_error",
    message: message || "Unknown window error",
    stack: event.error?.stack,
    severity: "high",
  });
});

window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
  const message = String((event.reason && event.reason.message) || event.reason || "");
  if (isChunkLoadError(message)) {
    event.preventDefault?.();
    forceChunkRecoveryReload("unhandledrejection");
    return;
  }
  reportClientError({
    type: "unhandled_rejection",
    message: message || "Unknown promise rejection",
    stack: event.reason?.stack,
    severity: "high",
  });
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <BrowserRouter basename={import.meta.env.VITE_BASE_PATH || "/patakare2/dist"}>
      <App />
    </BrowserRouter>
  </HelmetProvider>
);

// Clear stale service workers on preview/dev hosts to prevent blank-screen caching issues.
if (isPreviewOrDevHost()) {
  void clearPreviewServiceWorker();
} else if ("serviceWorker" in navigator) {
  // registerType:'autoUpdate' only installs+activates the new SW in the
  // background — it does NOT reload an already-open tab, so a returning
  // visitor's tab keeps running the OLD bundle (old CSS/JS/behavior)
  // indefinitely until they fully close and reopen it. Reload once the new
  // SW actually takes control, and re-check periodically for tabs left open
  // for a long time (e.g. a bookmarked/PWA-installed session).
  import("virtual:pwa-register").then(({ registerSW }) => {
    // "controllerchange" also fires on a tab's very first-ever service-worker
    // activation (clientsClaim() in sw.ts claims this page immediately), not
    // just on a genuine update — reloading unconditionally meant every
    // first-time visitor got an extra, unexplained reload right after their
    // first paint. Only reload when this tab was already under an OLDER
    // worker's control before this registration, i.e. an actual update.
    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded || !hadController) return;
      reloaded = true;
      window.location.reload();
    });

    const updateSW = registerSW({ immediate: true });
    window.setInterval(() => void updateSW(), 30 * 60 * 1000);
  });
}